import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import type React from 'react';
import type { ReactElement } from 'react';
import { ReactSortable } from 'react-sortablejs';

import type { ListItemBase } from '../../types.ts';

import { StyledCheckbox, StyledDragHandleIcon, StyledMenuItem } from './ListItemFieldsFilter.styled.ts';

export type ListItemField<T> = {
  displayName?: string;
  id: string;
  isHidden?: boolean;
  render?: (item: T, searchPhrase?: string) => ReactElement;
};

export type ListItemFieldsFilterProps<T> = {
  anchor: HTMLElement | null;
  fields: ListItemField<T>[];
  isDisabled?: boolean;
  onClick: (fieldId: string) => void;
  onOrderChange: (fields: ListItemField<T>[]) => void;
  setAnchor: (anchor: HTMLElement | null) => void;
  dataTest?: string;
};

export const ListItemFieldsFilter = <T extends ListItemBase>({
  anchor,
  fields,
  isDisabled = false,
  onClick,
  onOrderChange,
  setAnchor,
  dataTest,
}: ListItemFieldsFilterProps<T>) => {
  const isOpen = Boolean(anchor) && !isDisabled;

  const handleClose = () => setAnchor(null);

  return (
    <Menu
      anchorEl={anchor}
      anchorOrigin={{
        horizontal: 'right',
        vertical: 'bottom',
      }}
      onClose={handleClose}
      open={isOpen}
      transformOrigin={{
        horizontal: 'right',
        vertical: 'top',
      }}
      slotProps={{
        paper: {
          ...({
            'data-test': dataTest ?? 'manage-info-items-dialog',
          } as React.HTMLAttributes<HTMLDivElement>),
        },
      }}
    >
      <ReactSortable
        animation={300}
        handle=".MuiSvgIcon-root"
        list={fields}
        setList={updatedOrder => {
          onOrderChange?.(updatedOrder);
        }}
        swapThreshold={0.65}
      >
        {fields.map(({ displayName, id, isHidden }) => (
          <StyledMenuItem dense key={id} onClick={() => onClick(id)}>
            <StyledCheckbox checked={!isHidden} />
            <ListItemText primary={displayName || id} />
            <StyledDragHandleIcon />
          </StyledMenuItem>
        ))}
      </ReactSortable>
    </Menu>
  );
};
